import React from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { translate } from '../../types/EnumTranslations';
import { leaseObjectLabel } from '../../Helpers/leaseUtils';
import { naturalCompare } from '../../Helpers/naturalSort';
import { ClickModifiers } from '../../hooks/useOpenPath';
import { TenantRow, TenantSort, TenantSortKey } from './tenantsView';

const COLUMNS: { key: TenantSortKey; label: string }[] = [
    { key: 'name', label: 'Namn' },
    { key: 'objects', label: 'Objekt' },
];

interface TenantsTableProps {
    rows: TenantRow[];
    sort: TenantSort;
    onSort: (key: TenantSortKey) => void;
    selectedKey?: string;
    /** Names the building after each object; omitted while exactly one building is chosen. */
    buildingName?: (buildingKey: string) => string;
    onOpen: (tenantKey: string, event?: ClickModifiers) => void;
}

/**
 * The tenants of Hyresgäster as a sortable table; a row opens the tenant in
 * the pane. On phones each row becomes a card (Small Screens "Tables → cards").
 */
const TenantsTable: React.FC<TenantsTableProps> = ({ rows, sort, onSort, selectedKey, buildingName, onOpen }) => (
    <div className="std-box tenants-table-box">
    <table className="tenants-table">
        <thead>
            <tr>
                {COLUMNS.map(column => {
                    const active = sort.key === column.key;
                    return (
                        <th key={column.key} aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
                            <button type="button" className="tenants-table-sort" onClick={() => onSort(column.key)}>
                                {column.label}
                                {active && (sort.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                            </button>
                        </th>
                    );
                })}
                <th className="tenants-table-plain-header">Kontakt</th>
            </tr>
        </thead>
        <tbody>
            {rows.map(({ tenant, currentLeases }) => {
                const objects = [...currentLeases].sort((a, b) => naturalCompare(leaseObjectLabel(a), leaseObjectLabel(b)));
                return (
                    <tr
                        key={tenant.key}
                        className={`tenants-table-row ${tenant.key === selectedKey ? 'tenants-table-row--selected' : ''}`}
                        aria-selected={tenant.key === selectedKey}
                        tabIndex={0}
                        onClick={e => onOpen(tenant.key, e)}
                        onAuxClick={e => onOpen(tenant.key, e)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onOpen(tenant.key);
                            }
                        }}
                    >
                        <td className="tenants-cell-name">
                            <strong>{tenant.name}</strong>
                            <span className="tenants-muted">{translate(tenant.kind)}{tenant.orgNumber ? ` · ${tenant.orgNumber}` : ''}</span>
                        </td>
                        <td className="tenants-cell-objects">
                            {objects.length === 0 ? (
                                <span className="tenants-muted">Inget avtal</span>
                            ) : objects.map(lease => (
                                <span key={lease.key} className="tenants-object">
                                    {leaseObjectLabel(lease)}
                                    {buildingName && <span className="tenants-muted"> · {buildingName(lease.buildingKey)}</span>}
                                </span>
                            ))}
                        </td>
                        <td className="tenants-cell-contact">
                            <span>{tenant.email || <span className="tenants-muted">Ingen e-post</span>}</span>
                            <span>{tenant.phone || <span className="tenants-muted">Inget telefonnummer</span>}</span>
                        </td>
                    </tr>
                );
            })}
        </tbody>
    </table>
    </div>
);

export default TenantsTable;
